import { ApiError, notifySessionExpired } from "./apiClient";

/**
 * Jedna rozparsovaná SSE událost (`event:` / `data:` řádky jednoho bloku).
 */
export interface SseEvent {
  event: string | null;
  data: string | null;
}

/**
 * Konzumace SSE streamu z **POST** požadavku (Spring `SseEmitter` produces
 * `text/event-stream`). Původní vanilla-JS testovací nástroje (test-activation,
 * test-otp-password, test-admin) volají `/start` a čtou stream ručně přes
 * `ReadableStream` reader — tady je to sdílené místo pro všechny.
 *
 * Autentizace jde cookie `etts_access` (`credentials: "include"`). Endpointy leží
 * na API chainu, který má CSRF vypnutý, takže se žádný token neposílá.
 */
export async function streamSse(
  path: string,
  body: unknown,
  onEvent: (ev: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    // Stream čte fetch napřímo, takže 401 nespustí redirect uvnitř `apiFetch` —
    // bez tohohle by uživatel s vypršelou session viděl jen obecnou chybu.
    if (res.status === 401) {
      notifySessionExpired();
    }
    throw new ApiError(res.status, await res.text().catch(() => null), `HTTP ${res.status} ${path}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const emit = (chunk: string) => {
    if (!chunk.trim()) return;
    let event: string | null = null;
    let data: string | null = null;
    for (const line of chunk.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data = line.slice(5).trim();
    }
    onEvent({ event, data });
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? ""; // poslední (neúplný) blok zůstává v bufferu
    for (const chunk of chunks) emit(chunk);
  }

  // Flush: poslední událost (typicky COMPLETED) nemusí končit `\n\n` před
  // uzavřením streamu — jinak by zůstala v bufferu a výsledek by se nezobrazil.
  buffer += decoder.decode();
  if (buffer.trim()) emit(buffer);
}
